<template>
    <div class="point-select-single" ref="dropdownRef">
        <div class="custom-dropdown" :class="{ 'is-open': showDropdown }">
            <div
                class="custom-dropdown-trigger"
                :class="{ 'is-disabled': disabled }"
                @click="toggleDropdown"
            >
                <span class="trigger-text" :class="{ 'is-placeholder': !selectedPoint }">
                    {{ triggerLabel }}
                </span>
                <span v-if="selectedPoint" class="trigger-clear" @click.stop="clearSelection">
                    <i class="fas fa-times"></i>
                </span>
                <i v-else class="fas fa-chevron-down dropdown-icon"></i>
            </div>
            <div v-if="showDropdown" class="custom-dropdown-menu">
                <div class="dropdown-search">
                    <input
                        ref="searchInput"
                        v-model="searchKeyword"
                        type="text"
                        placeholder="관제점명 또는 주소로 검색..."
                        class="dropdown-search-input"
                        @click.stop
                    />
                    <i class="fas fa-search search-icon"></i>
                </div>
                <div class="dropdown-list">
                    <div v-if="filteredPoints.length === 0" class="dropdown-item no-results">
                        {{ loading ? "로딩 중..." : "검색 결과가 없습니다." }}
                    </div>
                    <div
                        v-for="point in filteredPoints"
                        :key="point.ptIdx"
                        class="dropdown-item"
                        :class="{ 'is-selected': value === point.ptAddr }"
                        @click="selectPoint(point)"
                    >
                        {{ point.ptName }} ({{ point.ptAddr }})
                    </div>
                </div>
            </div>
        </div>
        <span v-if="hint" class="field-hint">{{ hint }}</span>
    </div>
</template>

<script>
export default {
    name: "PointSelectDropdown",
    props: {
        value: {
            type: String,
            default: null,
        },
        points: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "관제점 선택",
        },
        hint: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            showDropdown: false,
            searchKeyword: "",
        };
    },
    computed: {
        selectedPoint() {
            if (!this.value) return null;
            return this.points.find((p) => p.ptAddr === this.value) || null;
        },
        triggerLabel() {
            if (!this.selectedPoint) return this.placeholder;
            return `${this.selectedPoint.ptName} (${this.selectedPoint.ptAddr})`;
        },
        filteredPoints() {
            if (!Array.isArray(this.points)) return [];
            if (!this.searchKeyword.trim()) return this.points;
            const kw = this.searchKeyword.toLowerCase().trim();
            return this.points.filter((p) => {
                return (p.ptName || "").toLowerCase().includes(kw) || (p.ptAddr || "").toLowerCase().includes(kw);
            });
        },
    },
    watch: {
        showDropdown(v) {
            if (v) {
                this.searchKeyword = "";
                this.$nextTick(() => {
                    if (this.$refs.searchInput) this.$refs.searchInput.focus();
                });
            }
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) return;
            this.showDropdown = !this.showDropdown;
        },
        selectPoint(point) {
            this.$emit("input", point.ptAddr);
            this.showDropdown = false;
        },
        clearSelection() {
            this.$emit("input", null);
        },
        handleClickOutside(event) {
            const ref = this.$refs.dropdownRef;
            if (ref && !ref.contains(event.target)) {
                this.showDropdown = false;
            }
        },
    },
};
</script>

<style scoped>
.point-select-single {
    width: 100%;
}

.custom-dropdown {
    position: relative;
    width: 100%;
}

.custom-dropdown-trigger {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-size: 12px;
    color: #1e293b;
    box-sizing: border-box;
}

.custom-dropdown-trigger:hover:not(.is-disabled) {
    border-color: #94a3b8;
}

.custom-dropdown-trigger.is-disabled {
    background: #f1f5f9;
    cursor: not-allowed;
    color: #94a3b8;
}

.custom-dropdown.is-open .custom-dropdown-trigger {
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.15);
}

.trigger-text {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.trigger-text.is-placeholder {
    color: #94a3b8;
}

.trigger-clear {
    color: #94a3b8;
    font-size: 11px;
    line-height: 1;
    padding: 2px;
    border-radius: 2px;
    flex-shrink: 0;
}

.trigger-clear:hover {
    color: #ef4444;
}

.dropdown-icon {
    font-size: 11px;
    color: #64748b;
    flex-shrink: 0;
    transition: transform 0.2s;
}

.custom-dropdown.is-open .dropdown-icon {
    transform: rotate(180deg);
}

.custom-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1100;
    max-height: 260px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.dropdown-search {
    position: relative;
    padding: 8px;
    border-bottom: 1px solid #e2e8f0;
}

.dropdown-search-input {
    width: 100%;
    padding: 6px 28px 6px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 12px;
    outline: none;
    box-sizing: border-box;
}

.dropdown-search-input:focus {
    border-color: #52b788;
}

.search-icon {
    position: absolute;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 12px;
}

.dropdown-list {
    max-height: 200px;
    overflow-y: auto;
    padding: 4px 0;
}

.dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 12px;
    color: #334155;
}

.dropdown-item:hover {
    background: #f1f5f9;
}

.dropdown-item.is-selected {
    background: #ecfdf5;
    color: #047857;
    font-weight: 500;
}

.dropdown-item.no-results {
    color: #94a3b8;
    text-align: center;
    cursor: default;
    font-style: italic;
}

.dropdown-item.no-results:hover {
    background: transparent;
}

.field-hint {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #94a3b8;
}
</style>
